import {Palette} from 'hubbl-shared';
import {memo, useCallback, useState} from 'react';
import styled from 'styled-components';
import {TextHeading7, Input, PrimaryButton, SecondaryButton} from '../index';
import {useI18n} from '../../hooks';

interface HubblTaskCreatorProps {
  onCreate: (title: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

const Container = styled.div`
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
  padding: 12px;
  width: 100%;
  box-sizing: border-box;
`;

const InputContainer = styled.div`
  width: 100%;
  margin-bottom: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

export const HubblTaskCreator = memo(
  ({onCreate, onCancel, isLoading = false}: HubblTaskCreatorProps) => {
    const [title, setTitle] = useState('');
    const t = useI18n();

    const handleCreate = useCallback(() => {
      if (title.trim()) {
        onCreate(title.trim());
        setTitle('');
      }
    }, [onCreate, title]);

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleCreate();
        } else if (e.key === 'Escape') {
          onCancel?.();
        }
      },
      [handleCreate, onCancel],
    );

    return (
      <Container>
        <InputContainer>
          <Input
            type="text"
            placeholder={t.common.whatNeedsToBeDone}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={handleKeyPress}
            isDisabled={isLoading}
            isCompact={true}
          />
        </InputContainer>
        <TextHeading7
          text={t.events.pressEnterToCreate}
          color={Palette.almostBlack500}
        />
        <Actions>
          {onCancel && (
            <SecondaryButton
              label={t.common.cancel}
              onClick={onCancel}
              isDisabled={isLoading}
              isCompact={true}
              customWidth={80}
            />
          )}
          <PrimaryButton
            label={isLoading ? t.common.creating : t.common.createTask}
            onClick={handleCreate}
            isDisabled={!title.trim() || isLoading}
            isCompact={true}
            customWidth={120}
          />
        </Actions>
      </Container>
    );
  },
);
